import React from "react";
import type { Descendant } from "slate";
import type { RenderElementProps, RenderLeafProps } from "slate-react";

import { useTextBlockContents } from "../TextBlockContext";
import { TextBlockRenderProvider, useTextBlockRendering } from "../TextBlockRenderContext";
import { TextBlockRenderOptions } from "../types";

interface TextBlockStaticProps {
  id: string;
  initialValue: Descendant[];
  options?: TextBlockRenderOptions;
}

const recursivelyRenderElement = (
  element: Descendant,
  options: ReturnType<typeof useTextBlockRendering>
): JSX.Element => {
  const { renderElement, renderLeaf } = options;
  const _leafAttributes = {};
  const leafAttributes = _leafAttributes as RenderLeafProps["attributes"];
  const _elementAttributes = {};
  const elementAttributes = _elementAttributes as RenderElementProps["attributes"];

  if ("text" in element) {
    return renderLeaf({
      children: element.text,
      leaf: element,
      text: {
        text: element.text
      },
      attributes: leafAttributes
    });
  } else {
    const childrenMapped = element.children?.map((child, i) => (
      <React.Fragment key={i}>{recursivelyRenderElement(child, options)}</React.Fragment>
    ));
    return renderElement({
      children: childrenMapped,
      element,
      attributes: elementAttributes
    });
  }
};

const recursivelyRenderElements = (
  elements: Descendant[],
  options: ReturnType<typeof useTextBlockRendering>
) => {
  return elements.map((element, i) => (
    <React.Fragment key={i}>{recursivelyRenderElement(element, options)}</React.Fragment>
  ));
};

const TextBlockStatic: React.FC<TextBlockStaticProps> = ({ id, initialValue, options }) => {
  const textBlockRendering = useTextBlockRendering();
  const { getInitialTextBlock } = useTextBlockContents();
  const contents = recursivelyRenderElements(
    getInitialTextBlock(id) || initialValue,
    textBlockRendering
  );

  if (options?.elements?.length === 1 && options.elements.includes("span")) {
    return <span>{contents}</span>;
  } else {
    return <div>{contents}</div>;
  }
};

export const renderTextBlockStatic: React.ComponentProps<
  typeof TextBlockRenderProvider
>["renderTextBlock"] = (id, initialValue, options) => {
  return <TextBlockStatic id={id} initialValue={initialValue} options={options} />;
};
